<template>
   <div id="rent-a-car-informations-container" class="gts-box">
      <div class="rent-a-car-informations-header-wrapper">
         <span class="car-title">{{ car.markModel }}</span>
         <span class="car-or"> {{ $t("rentacarOrderDetail_phOr") }}</span>
         <span class="car-similar"> {{ $t("rentacarOrderDetail_phSimilar") }}</span>
      </div>
      <!-- Body Wrapper -->
      <div class="rent-a-car-informations-body-wrapper">
         <div class="body-top">
            <section class="body-left">
               <img :src="car.imageUrl" alt="" srcset="" />
            </section>
            <section class="body-right">
               <!-- Location Informations -->
               <div class="location-informations-container">
                  <!-- PickUpLocation -->
                  <div class="location-informations-wrapper">
                     <span class="location-title txt-black">Pick Up</span>
                     <span class="location" v-for="(pickUpLocation, index) in PickUpLocations" :key="pickUpLocation">{{ pickUpLocation }}{{ index !== PickUpLocations.length - 1 ? "," : "" }}</span>
                     <span class="date">{{ car.pickUpDateTime | fullDateFormat }} - {{ car.pickUpDateTime | FormatterTime }}</span>
                  </div>
                  <!-- DropoffLocation -->
                  <div class="location-informations-wrapper">
                     <span class="location-title txt-black">Drop Off</span>
                     <span class="location" v-for="(dropOffLocation, index) in DropOffLocations" :key="dropOffLocation"
                        >{{ dropOffLocation }}{{ index !== DropOffLocations.length - 1 ? "," : "" }}</span
                     >
                     <span class="date">{{ car.dropOffDateTime | fullDateFormat }} - {{ car.dropOffDateTime | FormatterTime }}</span>
                  </div>
               </div>
            </section>
         </div>
         <div class="body-bottom">
            <ul class="car-features">
               <li class="icon-automatic">{{ $options.filters.ERacGearType(car.gearType) }}</li>
               <li class="icon-fuel">{{ $options.filters.ERacFuelType(car.fuelType) }}</li>
               <li class="icon-suitcases">{{ car.smallBaggageCapacity }} {{ $t("rentacar_suitCases") }}</li>
               <li class="icon-aircond">{{ $t("rentacar_airConditioning") }}</li>
               <li class="icon-passengers">{{ car.passengerCapacity }} {{ $t("rentacar_passengerCapacity") }}</li>
               <li class="icon-doors">{{ car.doorCount }} {{ $t("rentacar_doorCount") }}</li>
            </ul>
         </div>
      </div>

      <div class="rental-policies" v-if="hasVisiblePolicies">
         <div class="rental-policies__title">{{ $t("rentacar_rentalPolicies") }}</div>

         <ul>
            <li class="rental-policies-item" v-if="mileagePolicyLabel">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblMilleagePolicy") }}</div>
               <div class="rental-policies-item__description">{{ mileagePolicyLabel }}</div>
            </li>
            <li class="rental-policies-item" v-if="isNotNil(car?.deposit)">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblDeposit") }}</div>
               <div class="rental-policies-item__description">{{ car.deposit }}</div>
            </li>
            <li class="rental-policies-item" v-if="isNotNil(car?.cdwExcess)">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblCDWExcess") }}</div>
               <div class="rental-policies-item__description">{{ car.cdwExcess }}</div>
            </li>
            <li class="rental-policies-item" v-if="isNotNil(car?.fuelPolicyType) && car.fuelPolicyType != 0">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblFuelPolicy") }}</div>
               <div class="rental-policies-item__description">{{ $t("rentacar_lbl" + $options.filters.ERacFuelPolicyType(car.fuelPolicyType)) }}</div>
            </li>
         </ul>
      </div>

      <div class="rental-policies mandatory-fees" v-if="mandatoryFees.length">
         <div class="rental-policies__title">{{ $t("rentacar_mandatoryFeesTitle") }}</div>

         <ul>
            <li class="rental-policies-item" v-for="(fee, index) in mandatoryFees" :key="index" :class="{ localPayment: fee.localPayment }">
               <div class="rental-policies-tem-information-wrapper">
                  <div class="rental-policies-item-name-wrapper">
                     <span class="rental-policies-item-name">{{ fee.displayName }}</span>
                     <span class="rental-policies-item-badge" v-if="fee.localPayment">{{ $t("rentacar_localPayment") }}</span>
                  </div>

                  <span class="rental-policies-item-price">{{ fee.amount | price }}</span>
               </div>
            </li>
         </ul>
      </div>

      <div class="rental-policies optional-fees" v-if="optionalFees.length">
         <div class="rental-policies__title">{{ $t("rentacar_optionalFeesTitle") }}</div>

         <ul>
            <li class="rental-policies-item" v-for="(fee, index) in optionalFees" :key="index" :class="{ localPayment: fee.localPayment }">
               <div class="rental-policies-tem-information-wrapper">
                  <div class="rental-policies-item-name-wrapper">
                     <span class="rental-policies-item-name">{{ fee.displayName }}</span>
                     <span class="rental-policies-item-badge" v-if="fee.localPayment">{{ $t("rentacar_localPayment") }}</span>
                  </div>

                  <span class="rental-policies-item-price">{{ fee.amount | price }}</span>
               </div>
            </li>
         </ul>
      </div>

      <div class="rental-policies driver-requirements" v-if="hasVisibleDriverRequirements">
         <div class="rental-policies__title">{{ $t("rentacar_DriverRequirements") }}</div>

         <ul>
            <li class="rental-policies-item" v-if="isNotNil(car?.minLicenseAge)">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblMinLicenseAge") }}</div>
               <div class="rental-policies-item__description">{{ car.minLicenseAge }} {{ $t("rentacar_lblyears") }}</div>
            </li>
            <li class="rental-policies-item" v-if="isNotNil(car?.minDriverAge) && car?.minDriverAge != 0">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblMinDriverAge") }}</div>
               <div class="rental-policies-item__description">{{ car.minDriverAge }} {{ $t("rentacar_lblage") }}</div>
            </li>
            <li class="rental-policies-item" v-if="isNotNil(car?.maxDriverAge) && car?.maxDriverAge != 0">
               <div class="rental-policies-item__title">{{ $t("rentacar_lblMaxDriverAge") }}</div>
               <div class="rental-policies-item__description">{{ car.maxDriverAge }} {{ $t("rentacar_lblage") }}</div>
            </li>
         </ul>
      </div>
      <div class="ancillaries-wrapper">
         <RentACarOrderDetailAncillaries :ancillaries="ancillariesObj" :providerName="channelImplementationType == 301 ? 'pandora' : 'cizgi'" />
      </div>

      <!-- Credit Card Required + Price Widget -->
      <div class="rac-checkout-payment-row">
         <div class="rac-credit-card-alert" :class="{ invisible: !isCreditCardRequired }">
            <div class="rac-credit-card-alert__icon-col">
               <i class="gts-icon icon-rac-bank-creditcard"></i>
            </div>
            <div class="rac-credit-card-alert__body">
               <span class="rac-credit-card-alert__title">{{ $t("rentacar_creditCardRequired") }}</span>
               <span class="rac-credit-card-alert__description">{{ $t("rentacar_creditCardRequiredDescription") }}</span>
            </div>
         </div>
         <div class="rac-checkout-price-col">
            <PriceWidget label="rentacarCheckout" saleTotal :totalPriceInfo="TotalPriceInfo" />
         </div>
      </div>
   </div>
</template>

<script>
import RentACarOrderDetailAncillaries from "@/components/structural/rentacar/order-detail/RentACarOrderDetailAncillaries.vue";
import PriceWidget from "@/components/unitary/PriceWidget.vue";

export default {
   name: "CarInformations",
   components: { RentACarOrderDetailAncillaries, PriceWidget },
   props: {
      car: Object,
      ancillariesObj: Array,
      price: Object,
      channelImplementationType: Number,
      otherFees: Array,
      isCreditCardRequired: Boolean,
   },
   data() {
      return {
         bb: "",
         ancillaries: this.ancillariesObj,
      };
   },
   computed: {
      TotalPriceInfo() {
         return this.price;
      },
      PickUpLocations() {
         return this.car.pickUpLocationName?.split(",");
      },
      DropOffLocations() {
         return this.car.dropOffLocationName?.split(",");
      },
      hasVisibleDriverRequirements() {
         return this.isNotNil(this.car?.minLicenseAge) || this.isNotNil(this.car?.minDriverAge) || this.isNotNil(this.car?.maxDriverAge);
      },
      mileagePolicyLabel() {
         if (!this.car) return null;
         if (this.isNotNil(this.car.mileagePolicy)) return this.car.mileagePolicy;
         if (this.car.mileagePolicy == null && this.car.unlimitedMileage === true) return this.$t("ERacFacilityType_UNLIMITED_MILEAGE");
         return null;
      },
      mandatoryFees() {
         const fees = this.otherFees || [];

         return fees
            .filter((fee) => fee.mandatory)
            .map((fee) => ({
               displayName: fee.name,
               amount: fee.rentalPrice ?? null,
               badgeText: null,
               badgeClass: null,
               isMandatory: true,
               localPayment: fee.paymentTimingType === 2,
            }));
      },
      optionalFees() {
         const fees = this.otherFees || [];

         return fees
            .filter((fee) => !fee.mandatory)
            .map((fee) => ({
               displayName: fee.name,
               amount: fee.rentalPrice ?? null,
               badgeText: null,
               badgeClass: null,
               isMandatory: false,
               localPayment: fee.paymentTimingType === 2,
            }));
      },
      hasVisiblePolicies() {
         return this.mileagePolicyLabel || this.isNotNil(this.car?.deposit) || this.isNotNil(this.car?.cdwExcess) || (this.isNotNil(this.car?.fuelPolicyType) && this.car.fuelPolicyType != 0);
      },
   },
   methods: {
      isNotNil(val) {
         return val !== null && val !== undefined && val !== "";
      },
   },
};
</script>

<style lang="scss">
.dark-theme {
   .rental-policies {
      ul {
         .rental-policies-item {
            background-color: #1e1e1e !important;
            border: 1px solid #101010;
         }
      }
   }

   .mandatory-fees {
      ul {
         li {
            background-color: #1e1e1e !important;
            border: 1px solid #101010;

            &.localPayment {
               background-color: #2a1e1e !important;
               border: 1px solid #101010;
            }
         }
      }
   }

   .optional-fees {
      ul {
         li {
            background-color: #1e1e1e !important;
            border: 1px solid #101010;

            &.localPayment {
               background-color: #1e1e2a !important;
               border: 1px solid #101010;
            }
         }
      }
   }

   .rental-policies-item-name-wrapper .rental-policies-item-badge {
      color: white !important;
   }

   .card-price-wrapper .provider-logo {
      filter: none !important;
   }

   #rent-a-car-ancillaries-container ul.ancillaries-tabs-wrapper li .ancillary-tab-wrapper {
      background-color: #1e1e1e !important;
      border: 1px solid #101010;
   }
}

.ancillaries-wrapper {
   margin-bottom: 1.5rem !important;
   margin-top: 1.5rem !important;
}

.ancillaries-wrapper #rent-a-car-ancillaries-container {
   padding: 0 !important;
}
</style>

<style lang="scss" scoped>
#rent-a-car-informations-container {
   @media (max-width: 576px) {
      padding-left: 0;
      padding-right: 0;
   }

   .rent-a-car-informations-header-wrapper {
      .car-title {
         font-size: var(--large);
         font-weight: 800;
         color: var(--gray-800);
         text-transform: capitalize;
      }
      .car-similar {
         text-decoration: underline;
      }
      .car-features {
         font-weight: 600;
      }
   }

   .rent-a-car-informations-body-wrapper {
      margin-top: 48px;
      background: var(--gray-50) 0% 0% no-repeat padding-box;
      border: 1px solid var(--gray-300);
      border-radius: 8px;
      opacity: 1;
      margin-bottom: 1.5rem;

      @media (max-width: 576px) {
         margin-top: 1rem;
      }

      .body-top {
         display: flex;
         position: relative;

         @media (max-width: 576px) {
            flex-direction: column;
         }

         &::before {
            content: "*";
            position: absolute;
            height: 104px;
            background: var(--gray-800) 0% 0% no-repeat padding-box;
            border-radius: 0px 8px 8px 0px;
            opacity: 0.2;
            top: 0;
            bottom: 0;
            margin: auto;

            @media (max-width: 576px) {
               display: none;
            }
         }
         section {
            &.body-left {
               width: 40%;
               align-content: center;

               @media (max-width: 576px) {
                  width: 100%;
               }
            }
            &.body-right {
               border-left: 1px solid var(--gray-300);
               display: flex;
               align-items: center;
               justify-content: space-around;

               @media (max-width: 576px) {
                  border-left: none;
                  border-top: 1px solid var(--gray-300);
                  padding: 0.5rem;
               }

               .location-informations-container {
                  display: grid;
                  grid-template-columns: 2fr 1fr;

                  .location-informations-wrapper {
                     display: flex;
                     flex-direction: column;

                     .date {
                        font-weight: 500;
                     }
                  }
               }
            }
            width: 60%;

            @media (max-width: 576px) {
               width: 100%;
            }
         }
      }
   }

   .body-bottom {
      background: var(--gray-200) 0% 0% no-repeat padding-box;
      border-radius: 0px 0px 7px 7px;
      opacity: 1;
      ul.car-features {
         display: grid;
         grid-template-columns: repeat(6, 1fr);
         width: 100%;
         padding-left: 1.5rem;
         margin-top: 0rem;

         @media (max-width: 768px) {
            grid-template-columns: repeat(3, 1fr);
            padding-left: 0rem;
         }

         @media (max-width: 576px) {
            grid-template-columns: repeat(2, 1fr);
            padding-left: 0rem;
         }
         li {
            margin: 0.5rem;
            padding: 0.125rem 0.5rem;
            background-repeat: no-repeat;
            text-indent: 1.25rem;
            white-space: nowrap;
            font-size: var(--small);
            background-position-y: center;
            &.icon {
               &-automatic {
                  background-image: url("data:image/svg+xml;base64,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");
               }
               &-suitcases {
                  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNS43NDMiIGhlaWdodD0iMjQuNzg5IiB2aWV3Qm94PSIwIDAgMTUuNzQzIDI0Ljc4OSI+CiAgPGcgaWQ9Ikdyb3VwXzExNTU2IiBkYXRhLW5hbWU9Ikdyb3VwIDExNTU2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNzQ1LjYzOSAtMTgxLjIyOSkiPgogICAgPGcgaWQ9IkJhZ2dhZ2UiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc0NS42MzkgMTgxLjIyOSkiPgogICAgICA8cGF0aCBpZD0iRWxsaXBzZV8xNTYiIGRhdGEtbmFtZT0iRWxsaXBzZSAxNTYiIGQ9Ik0yLjI3NC0uNzVBMy4wMjQsMy4wMjQsMCwxLDEtLjc1LDIuMjc0LDMuMDI4LDMuMDI4LDAsMCwxLDIuMjc0LS43NVptMCw0LjUyNGExLjUsMS41LDAsMSwwLTEuNS0xLjVBMS41LDEuNSwwLDAsMCwyLjI3NCwzLjc3NFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIuMDkzIDE5LjQ5KSIgZmlsbD0iIzRiNGI0YiIvPgogICAgICA8cGF0aCBpZD0iRWxsaXBzZV8xNTgiIGRhdGEtbmFtZT0iRWxsaXBzZSAxNTgiIGQ9Ik0yLjIyNi0uNzVBMi45NzYsMi45NzYsMCwxLDEtLjc1LDIuMjI2LDIuOTc5LDIuOTc5LDAsMCwxLDIuMjI2LS43NVptMCw0LjQyN0ExLjQ1MSwxLjQ1MSwwLDEsMCwuNzc1LDIuMjI2LDEuNDUyLDEuNDUyLDAsMCwwLDIuMjI2LDMuNjc3WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoOS42MDMgMTkuNTYzKSIgZmlsbD0iIzRiNGI0YiIvPgogICAgICA8cGF0aCBpZD0iUGF0aF85ODgwIiBkYXRhLW5hbWU9IlBhdGggOTg4MCIgZD0iTTkuMDI4LjI1aDIuNTg1YTIuNywyLjcsMCwwLDEsMi43LDIuN1Y1LjAwNkg2LjMyN1YyLjk1MUEyLjcsMi43LDAsMCwxLDkuMDI4LjI1Wm0zLjc2MSwzLjIzMXYtLjUzYTEuMTc3LDEuMTc3LDAsMCwwLTEuMTc2LTEuMTc2SDkuMDI4QTEuMTc3LDEuMTc3LDAsMCwwLDcuODUyLDIuOTUxdi41M1oiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0yLjM5OSAtMC4yNSkiIGZpbGw9IiM0YjRiNGIiLz4KICAgICAgPHBhdGggaWQ9IlBhdGhfOTg4MSIgZGF0YS1uYW1lPSJQYXRoIDk4ODEiIGQ9Ik04LjIsMTMuNDg0QTIyLjU0NCwyMi41NDQsMCwwLDEsLjgzMywxMi4yNDFsLjUtMS40NDJBMjEuMDMyLDIxLjAzMiwwLDAsMCw4LjIsMTEuOTU5YTIwLjk4NSwyMC45ODUsMCwwLDAsNi44NTMtMS4xNmwuNSwxLjQ0MkEyMi41MDgsMjIuNTA4LDAsMCwxLDguMiwxMy40ODRaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMC4yNjcgLTMuOTY3KSIgZmlsbD0iIzRiNGI0YiIvPgogICAgICA8cGF0aCBpZD0iUGF0aF85ODgyIiBkYXRhLW5hbWU9IlBhdGggOTg4MiIgZD0iTTguMTg5LDE5LjQ4NEEyMi41NDUsMjIuNTQ1LDAsMCwxLC44MzMsMTguMjQxbC41LTEuNDQyYTIwLjg3NiwyMC44NzYsMCwwLDAsMTMuNzIsMGwuNSwxLjQ0MkEyMi41NDUsMjIuNTQ1LDAsMCwxLDguMTg5LDE5LjQ4NFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0wLjI2NyAtNS4xMzQpIiBmaWxsPSIjNGI0YjRiIi8+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzk4ODQiIGRhdGEtbmFtZT0iUGF0aCA5ODg0IiBkPSJNMTIuNzg5LDM0Ljc2NUg5LjgyNFYzMy4yMzloMi45NjVaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMy4zNTggLTEyLjU2NykiIGZpbGw9IiM0YjRiNGIiLz4KICAgICAgPHBhdGggaWQ9IlBhdGhfOTg4NSIgZGF0YS1uYW1lPSJQYXRoIDk4ODUiIGQ9Ik0yLjYyOSwyNS4xMjRhMy4xNTksMy4xNTksMCwwLDEtMi4zOC0zLjA4MlY5LjQyNUEzLjE1NywzLjE1NywwLDAsMSwzLjM4LDYuMjVoOS40NzhhMy4xNTcsMy4xNTcsMCwwLDEsMy4xMzIsMy4xNzVWMjIuMDQyYTMuMTU3LDMuMTU3LDAsMCwxLTIuMiwzLjAzbC0uNDYxLTEuNDU0YTEuNjM3LDEuNjM3LDAsMCwwLDEuMTMyLTEuNTc2VjkuNDI1YTEuNjMsMS42MywwLDAsMC0xLjYwNy0xLjY1SDMuMzhhMS42MywxLjYzLDAsMCwwLTEuNjA3LDEuNjVWMjIuMDQyQTEuNjM2LDEuNjM2LDAsMCwwLDMsMjMuNjQ0WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTAuMjQ4IC0zLjAxOSkiIGZpbGw9IiM0YjRiNGIiLz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPgo=");
               }
               &-aircond {
                  background-image: url("data:image/svg+xml;base64,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");
               }
               &-passengers {
                  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIxNy45NTEiIHZpZXdCb3g9IjAgMCAyNCAxNy45NTEiPgogIDxnIGlkPSJpY29uLWFjdGl2ZS1hZ2VudHMiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0zNC4zMjcgLTMwLjcxNCkiPgogICAgPHBhdGggaWQ9IkVsbGlwc2VfMTI2OSIgZGF0YS1uYW1lPSJFbGxpcHNlIDEyNjkiIGQ9Ik0zLjg3My0uNzVBNC42MjMsNC42MjMsMCwxLDEtLjc1LDMuODczLDQuNjI4LDQuNjI4LDAsMCwxLDMuODczLS43NVptMCw3Ljk3M2EzLjM1LDMuMzUsMCwxLDAtMy4zNS0zLjM1QTMuMzU0LDMuMzU0LDAsMCwwLDMuODczLDcuMjIzWiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMzguMzQ5IDMxLjQ2NCkiIGZpbGw9IiMzNDM0MzQiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwMzQ0IiBkYXRhLW5hbWU9IlBhdGggMTAzNDQiIGQ9Ik01MC4xMTgsNTIuMjg3SDQ4Ljg0NWE2LjYyNCw2LjYyNCwwLDEsMC0xMy4yNDUsMEgzNC4zMjdhNy45LDcuOSwwLDEsMSwxNS43OTEsMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAgLTMuNjIyKSIgZmlsbD0iIzM0MzQzNCIvPgogICAgPHBhdGggaWQ9IkVsbGlwc2VfMTI3MSIgZGF0YS1uYW1lPSJFbGxpcHNlIDEyNzEiIGQ9Ik0zLjE0MS0uNzVBMy44OTEsMy44OTEsMCwxLDEtLjc1LDMuMTQxLDMuOSwzLjksMCwwLDEsMy4xNDEtLjc1Wm0wLDYuNTA5QTIuNjE4LDIuNjE4LDAsMSwwLC41MjMsMy4xNDEsMi42MjEsMi42MjEsMCwwLDAsMy4xNDEsNS43NTlaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg0OC42MjQgMzIuMTk2KSIgZmlsbD0iIzM0MzQzNCIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTAzNDYiIGRhdGEtbmFtZT0iUGF0aCAxMDM0NiIgZD0iTTYyLjY5Myw0OS45NzVINjEuNDIxYTUuNDI1LDUuNDI1LDAsMCwwLTEuNjQyLTMuOTE5LDUuMiw1LjIsMCwwLDAtMy42NDctMS40OTMsNS4wMTQsNS4wMTQsMCwwLDAtMi44NjQuODZsLS4wMDYsMGE1Ljc0Niw1Ljc0NiwwLDAsMC0xLjUxMywxLjUxN2wtMS4wNjEtLjdBNy4wMjcsNy4wMjcsMCwwLDEsNTIuNTA5LDQ0LjRhNi4yNTUsNi4yNTUsMCwwLDEsMy42MjItMS4xMSw2LjQ3LDYuNDcsMCwwLDEsNC41MzYsMS44NTRBNi42ODgsNi42ODgsMCwwLDEsNjIuNjkzLDQ5Ljk3NVoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC00LjM2NyAtMy4zNjgpIiBmaWxsPSIjMzQzNDM0Ii8+CiAgPC9nPgo8L3N2Zz4K");
               }
               &-fuel {
                  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOC45MzIiIGhlaWdodD0iMTkiIHZpZXdCb3g9IjAgMCAxOC45MzIgMTkiPgogIDxnIGlkPSJHcm91cF8xMTU1NyIgZGF0YS1uYW1lPSJHcm91cCAxMTU1NyIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTc0NCAtMjE5KSI+CiAgICA8cGF0aCBpZD0iUmVjdGFuZ2xlXzQzNjFfLV9PdXRsaW5lIiBkYXRhLW5hbWU9IlJlY3RhbmdsZSA0MzYxIC0gT3V0bGluZSIgZD0iTTIuNzE0LDEuMzU3QTEuMzU5LDEuMzU5LDAsMCwwLDEuMzU3LDIuNzE0VjE2LjczOEgxMi4yMTRWMi43MTRhMS4zNTksMS4zNTksMCwwLDAtMS4zNTctMS4zNTdIMi43MTRNMi43MTQsMGg4LjE0M2EyLjcxNCwyLjcxNCwwLDAsMSwyLjcxNCwyLjcxNFYxOC4xSDBWMi43MTRBMi43MTQsMi43MTQsMCwwLDEsMi43MTQsMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc0NS44MSAyMTkpIiBmaWxsPSIjMzYzNjM2Ii8+CiAgICA8cGF0aCBpZD0iUGF0aF8xMDk1NSIgZGF0YS1uYW1lPSJQYXRoIDEwOTU1IiBkPSJNMi41MzMsMGgxMS40YTIuNTMzLDIuNTMzLDAsMCwxLDIuNTMzLDIuNTMzVjMuOEgwVjIuNTMzQTIuNTMzLDIuNTMzLDAsMCwxLDIuNTMzLDBaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg3NDQgMjM0LjIpIiBmaWxsPSIjZmZmIi8+CiAgICA8cGF0aCBpZD0iUmVjdGFuZ2xlXzQzNjNfLV9PdXRsaW5lIiBkYXRhLW5hbWU9IlJlY3RhbmdsZSA0MzYzIC0gT3V0bGluZSIgZD0iTTEuODEsMS4zNTdhLjQ1My40NTMsMCwwLDAtLjQ1Mi40NTJ2LjQ1MkgxNS44MzNWMS44MWEuNDUzLjQ1MywwLDAsMC0uNDUyLS40NTJIMS44MU0xLjgxLDBIMTUuMzgxYTEuODEsMS44MSwwLDAsMSwxLjgxLDEuODF2MS44MUgwVjEuODFBMS44MSwxLjgxLDAsMCwxLDEuODEsMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc0NCAyMzQuMzgxKSIgZmlsbD0iIzM2MzYzNiIvPgogICAgPHBhdGggaWQ9IlJlY3RhbmdsZV80MzYyXy1fT3V0bGluZSIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgNDM2MiAtIE91dGxpbmUiIGQ9Ik0xLjgxLDEuMzU3YS40NTMuNDUzLDAsMCwwLS40NTIuNDUydjEuODFhLjQ1My40NTMsMCwwLDAsLjQ1Mi40NTJINi4zMzNhLjQ1My40NTMsMCwwLDAsLjQ1Mi0uNDUyVjEuODFhLjQ1My40NTMsMCwwLDAtLjQ1Mi0uNDUySDEuODFNMS44MSwwSDYuMzMzYTEuODEsMS44MSwwLDAsMSwxLjgxLDEuODF2MS44MWExLjgxLDEuODEsMCwwLDEtMS44MSwxLjgxSDEuODFBMS44MSwxLjgxLDAsMCwxLDAsMy42MTlWMS44MUExLjgxLDEuODEsMCwwLDEsMS44MSwwWiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoNzQ4LjUyNCAyMjEuNzE0KSIgZmlsbD0iIzM2MzYzNiIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA5NTIiIGRhdGEtbmFtZT0iUGF0aCAxMDk1MiIgZD0iTS42NzksMy4xMDVBLjY3OC42NzgsMCwwLDEsMCwyLjQyN1YuNjc5YS42NzkuNjc5LDAsMCwxLDEuMzU3LDBWMi40MjdBLjY3OS42NzksMCwwLDEsLjY3OSwzLjEwNVoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc2MS44NTUgMjI3LjM2KSByb3RhdGUoMTgwKSIgZmlsbD0iIzM0MzQzNCIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA5NTEiIGRhdGEtbmFtZT0iUGF0aCAxMDk1MSIgZD0iTTYxNTguNTYzLDI2NS4xMDloLTQuMzI2di0xLjM1N2gyLjk2OVYyNTguM2wtMy4zNzgtMi41NDkuODE4LTEuMDgzLDMuOTE3LDIuOTU2WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTUzOTUuNjMxIC0zMi41MDMpIiBmaWxsPSIjMzQzNDM0Ii8+CiAgPC9nPgo8L3N2Zz4K");
               }
               &-doors {
                  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIxOS44MTMiIHZpZXdCb3g9IjAgMCAyMCAxOS44MTMiPgogIDxnIGlkPSJHcm91cF8xMTU1OCIgZGF0YS1uYW1lPSJHcm91cCAxMTU1OCIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTc0NCAtMjYxLjE3OCkiPgogICAgPHBhdGggaWQ9IlBhdGhfMTA5NTMiIGRhdGEtbmFtZT0iUGF0aCAxMDk1MyIgZD0iTTEwLjE2OCwxLjExN2ExNy4zNjgsMTcuMzY4LDAsMCwwLTMuNDgzLjMyN2MtLjczOS4xNTItMS4zNjUuMjYzLTEuOTE4LjM2MUE3LjU2OSw3LjU2OSwwLDAsMC0uMzQsNC4yOTRDLTIuNDIsNi4yMTEtMy42LDkuNTI4LTQuMDA4LDEwLjg0M2wzLjY5MSw2LjkyOUgxMi43MTlWMS4yNjFhMjMuMSwyMy4xLDAsMCwwLTIuNTUxLS4xNDRtMC0xLjU3OUEyMy4xMTcsMjMuMTE3LDAsMCwxLDE0LjMtLjFWMTkuMzUxSC0xLjI2NUwtNS43LDExLjAyMnMxLjI3NC01LjEwOCw0LjI5Mi03Ljg4OVMyLjQzOS43MDYsNi4zNjYtLjFBMTkuMDMxLDE5LjAzMSwwLDAsMSwxMC4xNjgtLjQ2MloiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc0OS43MDIgMjYxLjY0KSIgZmlsbD0iIzM0MzQzNCIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA5NTQiIGRhdGEtbmFtZT0iUGF0aCAxMDk1NCIgZD0iTTEuNzYxLDEuNTc5QTguMjc3LDguMjc3LDAsMCwwLTIuMTM0LDMuMjI2LDYuNDExLDYuNDExLDAsMCwwLTMuNDgxLDUuMkg0LjkwOFYxLjU3OUgxLjc2MU0xLjY1MywwSDYuNDg3VjYuNzhILTUuN0ExMC4zNTcsMTAuMzU3LDAsMCwxLTMuMiwyLjA2NSwxMC4wMDUsMTAuMDA1LDAsMCwxLDEuNjUzLDBaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg3NTQuNSAyNjQuNDY4KSIgZmlsbD0iIzM0MzQzNCIvPgogICAgPHBhdGggaWQ9IkxpbmVfNDgzIiBkYXRhLW5hbWU9IkxpbmUgNDgzIiBkPSJNMy4xNTIuODI5SC4wMzlhLjc4OS43ODksMCwwLDEsMC0xLjU3OUgzLjE1MmEuNzg5Ljc4OSwwLDAsMSwwLDEuNTc5WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoNzU2LjYzNyAyNzMuOTQ4KSIgZmlsbD0iIzM0MzQzNCIvPgogIDwvZz4KPC9zdmc+Cg==");
               }
            }
         }
      }
   }
}

.rental-policies {
   flex-direction: column;
   margin-bottom: 1rem;
   margin-top: 1rem;
   // padding: 1rem;

   @media (max-width: 576px) {
      padding-left: 0;
      padding-right: 0;
   }

   &__title {
      font-weight: 500;
      margin-bottom: 0.5rem;
      font-size: var(--normal);
      color: var(--gray-800);
   }

   ul {
      display: flex;
      gap: 1rem;

      @media (max-width: 576px) {
         flex-wrap: wrap;
      }

      .rental-policies-item {
         background-color: #f1f5f9;
         border-radius: 0.75rem;
         padding: 1rem 1rem;
         width: fit-content;
         line-height: 1.35rem;

         @media (max-width: 576px) {
            flex-basis: calc(50% - 0.5rem);
         }

         &__title {
            font-size: var(--small);
            color: var(--gray-700);
         }
         &__description {
            font-weight: 600;
         }
      }
   }
}

.mandatory-fees {
   ul {
      flex-wrap: wrap;

      li {
         display: flex;
         gap: 1rem;
         border: none !important;

         align-items: flex-start;
         background-color: #eff6ff;
         flex-basis: fit-content;

         @media (max-width: 576px) {
            flex-basis: 100%;
         }

         .rental-policies-tem-information-wrapper {
            display: flex;
            flex-direction: column;

            .rental-policies-item-name-wrapper {
               display: flex;
               gap: 0.75rem;

               @media (max-width: 576px) {
                  flex-direction: column;
                  gap: 0rem;
                  margin-bottom: 1rem;
               }

               .rental-policies-item-name {
                  font-weight: 500;
               }
               .rental-policies-item-badge {
                  background-color: #ff6900;
                  color: white;
                  padding: 0 0.5rem;
                  border-radius: 0.25rem;
                  font-size: var(--small);
                  font-weight: 600;
               }
            }
         }

         .rental-policies-item-price {
            font-weight: 800;
         }

         &.localPayment {
            background-color: #fff7ed;
            color: #f54900;

            @media (max-width: 576px) {
               flex-basis: 100% !important;
            }
         }
      }
   }
}

.optional-fees {
   margin-top: 0 !important;
   padding-top: 0 !important;

   @media (max-width: 576px) {
      margin-bottom: 1.5rem;
   }

   ul {
      gap: 1rem;

      @media (max-width: 576px) {
         flex-wrap: wrap;
      }

      li {
         display: flex;
         gap: 1rem;
         border: none !important;
         align-items: flex-start;

         @media (max-width: 576px) {
            flex-basis: 100% !important;
            width: 100% !important;
         }

         .rental-policies-tem-information-wrapper {
            display: flex;
            flex-direction: column;

            .rental-policies-item-name-wrapper {
               display: flex;
               gap: 0.75rem;

               @media (max-width: 576px) {
                  flex-direction: column;
                  gap: 0rem;
                  margin-bottom: 1rem;
               }

               .rental-policies-item-name {
                  font-weight: 500;
               }
               .rental-policies-item-badge {
                  background-color: #ff6900;
                  color: white;
                  padding: 0 0.5rem;
                  border-radius: 0.25rem;
                  font-size: var(--small);
                  font-weight: 600;
               }
            }
         }

         .rental-policies-item-price {
            font-weight: 800;
         }

         &.localPayment {
         }
      }
   }
}

.rac-checkout-payment-row {
   display: flex;
   align-items: flex-start;
   gap: 1.5rem;
   padding-top: 1.5rem;
   border-top: 1px solid var(--gray-300);

   @media (max-width: 768px) {
      flex-direction: column;
   }

   .rac-credit-card-alert {
      flex: 1 1 0;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      background: var(--yellow-0);
      border: 1px solid var(--yellow-400);
      border-radius: 1rem;
      padding: 1rem 1.5rem 1rem 0;
      align-self: flex-start;
      margin-top: 0;

      &__icon-col {
         flex-shrink: 0;
         display: flex;
         align-items: center;
         justify-content: center;
         width: 3.5rem;
         padding: 0.25rem 0.75rem;

         i {
            font-size: 1.875rem;
            color: var(--orange-500);
         }
      }

      &__body {
         display: flex;
         flex-direction: column;
         gap: 0.25rem;
         padding-top: 0.25rem;
         padding-bottom: 0.5rem;
      }

      &__title {
         font-size: var(--normal);
         font-weight: 600;
         color: var(--orange-500);
         line-height: 1.25rem;
         white-space: nowrap;
      }

      &__description {
         font-size: var(--xsmall);
         color: var(--orange-500);
         line-height: 1.125rem;
      }
   }

   .rac-checkout-price-col {
      flex: 0 0 40%;
      max-width: 40%;

      @media (max-width: 768px) {
         flex: 0 0 100%;
         max-width: 100%;
      }
      @media (max-width: 576px) {
         width: 100%;
      }
   }
}

.driver-requirements {
   margin-bottom: 0 !important;
   margin-top: 0;

   ul {
      li {
         display: flex;
         flex-direction: column;
         gap: 0.5rem;
         line-height: 0.75rem !important;
         border: none !important;
         background-color: #eef2ff;
      }
   }
}

.ancillaries-wrapper {
   margin-bottom: 1rem !important;
   ::v-deep #rent-a-car-ancillaries-container {
      padding: 0 !important;
      margin-bottom: 1rem !important;
   }
}
</style>
